import { createStore } from 'zustand';
import { abandonAnytimeTransaction } from 'Services/Api/_HumanWritten/AnytimeTicketService';
import { store as globalStore } from 'Models/Store';
import { GiftCertificateUsageEntity } from 'Models/Entities';
import { AnytimeProductType } from '../Queries/useAnytimeProductList';

export type ModalStep =
	| 'login'
	| 'select_product'
	| 'register_customer'
	| 'terms'
	// #2613: where a credit voucher is applied. It must be settled before the payment step mounts —
	// StripeProvider keys its <Elements> tree on the total, so changing the price on the payment step
	// remounts the payment form and wipes anything the customer has typed.
	| 'review'
	| 'payment'
	| 'success';

export interface SelectCustomer {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
}

export interface AnytimeTicketState {
	step: ModalStep;
	selectedProductId: string | null;
	selectedProductType: AnytimeProductType | null;
	selectedCustomerId: string | null;
	selectedCustomer: SelectCustomer | null;
	customerError: string | null;
	productQuantity: number;
	transactionId: string;
	userEntitlementId: string | null;
	purchasedItemId: string | null;
	termsAccepted: boolean;
	/** How many times Continue has been pressed with the terms unticked. The terms step highlights
	 *  and scrolls to the checkbox while this is above zero and the box is still unticked — a count
	 *  rather than a flag so a second press re-scrolls after the customer has scrolled away. */
	termsPromptCount: number;
	registrationComplete: boolean;
	registerCustomerLoading: boolean;
	loginMode: string;
	loginLoading: boolean;
	isNavBlocked: boolean;
	loginResetFn: (() => void) | null;
	/** Credit-voucher usages applied to this purchase (#2613); authoritative amounts come from the server. */
	giftCertUsages: GiftCertificateUsageEntity[];
}

export interface AnytimeTicketActions {
	// User Intents
	selectProduct: (id: string | null, type?: AnytimeProductType | null) => void;
	setProductQuantity: (quantity: number) => void;
	selectCustomer: (customer: SelectCustomer | null) => void;
	setCustomerError: (error: string | null) => void;
	startRegisterCustomer: () => void;
	cancelRegisterCustomer: () => void;
	completeRegisterCustomer: (customer: SelectCustomer) => void;
	completeLogin: () => void;
	goToTerms: () => void;
	acceptTerms: (accepted: boolean) => void;
	/** Raise the "you haven't accepted the terms" prompt on the terms step. */
	promptForTerms: () => void;
	setGiftCertUsages: (usages: GiftCertificateUsageEntity[]) => void;
	goToPayment: () => void;
	/** Back out of the payment step to review, keeping the transaction. */
	goToReview: () => void;
	startCheckout: (transactionId: string, userEntitlementId: string | null, purchasedItemId: string | null) => void;
	finishPurchase: () => void;
	abortPurchase: () => void;
	triggerLoginReset: () => void;

	// Internal State Helpers
	setRegisterCustomerLoading: (loading: boolean) => void;
	setRegistrationComplete: (complete: boolean) => void;
	setLoginMode: (mode: string) => void;
	setLoginLoading: (loading: boolean) => void;
	setLoginResetFn: (fn: (() => void) | null) => void;
	resetStore: () => void;
}

export type AnytimeTicketStore = AnytimeTicketState & AnytimeTicketActions;

export const createAnytimeTicketStore = () => {
	const initialState: AnytimeTicketState = {
		step: globalStore.loggedIn ? 'select_product' : 'login',
		selectedProductId: null,
		selectedProductType: null,
		selectedCustomerId: null,
		selectedCustomer: null,
		customerError: null,
		productQuantity: 1,
		transactionId: '',
		userEntitlementId: null,
		purchasedItemId: null,
		termsAccepted: false,
		termsPromptCount: 0,
		registrationComplete: false,
		registerCustomerLoading: false,
		loginMode: 'initial',
		loginLoading: false,
		isNavBlocked: false,
		loginResetFn: null,
		giftCertUsages: [],
	};

	return createStore<AnytimeTicketStore>((set, get) => ({
		...initialState,
		selectProduct: (selectedProductId, selectedProductType = 'TICKET') =>
			set({
				selectedProductId,
				selectedProductType,
				termsAccepted: false,
				termsPromptCount: 0,
				productQuantity: 1,
			}),
		setProductQuantity: productQuantity => set({ productQuantity }),
		selectCustomer: selectedCustomer => {
			const { selectedProductType, selectedProductId } = get();
			// Ticket options are hidden until a customer is chosen; if the customer is
			// cleared while a ticket was selected, drop that (now-hidden) selection.
			const clearingTicketSelection = !selectedCustomer && selectedProductType === 'TICKET';
			set({
				selectedCustomer,
				selectedCustomerId: selectedCustomer?.id || null,
				selectedProductId: clearingTicketSelection ? null : selectedProductId,
				selectedProductType: clearingTicketSelection ? null : selectedProductType,
				// Clear any validation error once a customer is chosen.
				customerError: selectedCustomer ? null : get().customerError,
			});
		},
		setCustomerError: customerError => set({ customerError }),
		startRegisterCustomer: () =>
			set({
				step: 'register_customer',
				isNavBlocked: true,
			}),
		cancelRegisterCustomer: () =>
			set({
				step: 'select_product',
				registerCustomerLoading: false,
			}),
		completeRegisterCustomer: customer =>
			set({
				selectedCustomer: customer,
				selectedCustomerId: customer.id,
				step: 'select_product',
				registerCustomerLoading: false,
			}),
		completeLogin: () =>
			set({
				step: 'select_product',
				isNavBlocked: true,
			}),
		goToTerms: () =>
			set({
				step: 'terms',
				isNavBlocked: true,
				termsPromptCount: 0,
			}),
		acceptTerms: (accepted: boolean) => set({ termsAccepted: accepted }),
		promptForTerms: () => set({ termsPromptCount: get().termsPromptCount + 1 }),
		// Lands on review, not payment: the transaction exists by now (the terms/product footer created
		// it), so a credit can be applied while the price is still being quoted.
		startCheckout: (transactionId, userEntitlementId, purchasedItemId) =>
			set({
				transactionId,
				userEntitlementId,
				purchasedItemId,
				step: 'review',
				isNavBlocked: true,
				giftCertUsages: [],
			}),
		setGiftCertUsages: giftCertUsages => set({ giftCertUsages }),
		goToPayment: () => set({ step: 'payment', isNavBlocked: true }),
		// Payment's Back button. Deliberately not abortPurchase: the transaction is still live and the
		// customer is stepping back to change the credit voucher they applied, not abandoning the
		// purchase. Abandoning here sent them to product selection and threw the transaction away.
		goToReview: () => set({ step: 'review', isNavBlocked: true }),
		finishPurchase: () =>
			set({
				step: 'success',
				isNavBlocked: false,
			}),
		abortPurchase: () => {
			const { transactionId, loginMode } = get();
			if (transactionId) {
				abandonAnytimeTransaction(transactionId).catch(() => {});
			}

			// If they were in the middle of a purchase, go back to product selection
			// If they were just starting, stay where they are or reset
			set({
				transactionId: '',
				userEntitlementId: null,
				purchasedItemId: null,
				selectedProductId: null,
				selectedProductType: null,
				productQuantity: 1,
				isNavBlocked: false,
				giftCertUsages: [],
				step: globalStore.loggedIn || loginMode === 'initial' ? 'select_product' : 'login',
			});
		},
		triggerLoginReset: () => {
			const { loginResetFn } = get();
			loginResetFn?.();
		},
		setRegisterCustomerLoading: registerCustomerLoading => set({ registerCustomerLoading }),
		setRegistrationComplete: registrationComplete => set({ registrationComplete }),
		setLoginMode: loginMode => set({ loginMode }),
		setLoginLoading: loginLoading => set({ loginLoading }),
		setLoginResetFn: loginResetFn => set({ loginResetFn }),
		resetStore: () => set(initialState),
	}));
};
